import { addBreadcrumb, captureException, captureMessage } from "@sentry/nextjs";
import { differenceInYears, parse } from "date-fns";
import { assign, emit, enqueueActions, setup } from "xstate";

import { VISIT_REASON_PATHS } from "@/constants";
import {
  DigitalVisitReasonEnum,
  NearByV4Response,
  SlotsSearchResponseV4,
  ValidatePatientResponseWithScheduledAppointments,
} from "@/lib/__generated/api";
import type { BffFacilityFieldsFragment } from "@/lib/__generated/beffe.sdk";
import type { QuestionnaireByQuery } from "@/lib/__generated/enterpriseGraph.sdk";
import { dataLayerPush } from "@/lib/utils/dataLayerHelpers";
import {
  getAppointmentDataLayerParams,
  getBaseDataLayerParams,
  getDataLayerBrand,
} from "@/lib/utils/dataLayerParams";
import type {
  AppointmentData,
  GuarantorData,
  Language,
  NavigableStep,
  NeedBasedQuestionAnswers,
  PatientData,
  SchedulerContext,
  SchedulerEvent,
  StepMeta,
} from "@/types/scheduler-machine";

import { REASON_FOR_VISIT_MAPPING, DataLayerStepNames } from "../constants";
import { formEvent } from "../services/formEvents";
import { userEvent } from "../services/userEvents";
import { trackFormValidationError } from "../utils/form-analytics";
import {
  buildFieldValidationErrors,
  buildNeedsBasedQuestionValidationErrors,
  buildStepValidationError,
  pushFormValidationError,
} from "../utils/form-validation-datalayer";
import type { FormValidationErrorItem } from "../utils/form-validation-datalayer";
import { validateGuarantorData } from "./guarantor-data-validation";
import { validatePatientData } from "./patient-data-validation";
import { fetchQuestionsActor } from "./utils/fetchQuestions.util";
import { fetchNearbySlotsActor, fetchSlotsActor } from "./utils/fetchSlots.util";
import { getFacilityLocationsActor } from "./utils/getFacilityLocations.util";
import { isPatientUnder18 } from "./utils/patient-age.util";
import { scheduleAppointmentActor } from "./utils/scheduleAppointment.util";
import {
  getSchedulerFlow,
  isSlotFirstFlow,
  requiresNeedsBasedQuestions,
} from "./utils/scheduler-flow.util";
import { buildSlotsSearchInput } from "./utils/slotsSearchInput.util";
import { transunionFraudCheckActor } from "./utils/transunionFraudCheck.util";
import { validatePatientActor } from "./utils/validatePatient.util";
import { verifyRecaptchaActor } from "./utils/verifyRecaptcha.util";
import { writeAppointmentCookie as writeAppointmentCookieUtil } from "./utils/writeAppointmentCookie.util";

export function isAgeIneligibleForReason(
  dob: string | undefined,
  reason: DigitalVisitReasonEnum | null,
): boolean {
  if (!dob || !reason) return false;
  const age = differenceInYears(new Date(), parse(dob, "MM/dd/yyyy", new Date()));
  const { ClearAligners, Dentures, Implants } = DigitalVisitReasonEnum;
  if (age < 16 && reason === ClearAligners) return true;
  if (age < 18 && (reason === Dentures || reason === Implants)) return true;
  return false;
}

type FacilityAddress = {
  address1?: string | null;
  line1?: string | null;
  city?: string | null;
  state?: string | null;
  stateCode?: string | null;
  zipCode?: string | null;
};

export function buildConfirmationUrl(
  reason: string | null | undefined,
  address: FacilityAddress | null | undefined,
): string | null {
  const street = address?.address1 || address?.line1;
  const state = address?.stateCode || address?.state;

  if (!reason || !state || !address?.city || !street) return null;

  const slugify = (str: string) =>
    str
      .replaceAll(/_/g, "-")
      .replaceAll(/([a-z])([A-Z])/g, "$1-$2")
      .toLowerCase()
      .replaceAll(/[^a-z0-9-]/g, "-")
      .replaceAll(/(?:-+)/g, "-")
      .replaceAll(/^-|-$/g, "");

  const reasonSlug =
    reason === DigitalVisitReasonEnum.HealthyMouthMovement
      ? (VISIT_REASON_PATHS[DigitalVisitReasonEnum.HealthyMouthMovement]?.[0] ?? slugify(reason))
      : slugify(reason);
  return `/${reasonSlug}/${state.toLowerCase()}/${slugify(address.city)}/${slugify(street)}/confirmation`;
}

// Actions, guards and actors shared by the machine and its step files. Each step wraps its
// config in `schedulerSetup.createStateConfig` so event and action types are checked in place.
export const schedulerSetup = setup({
  types: {
    context: {} as SchedulerContext,
    events: {} as SchedulerEvent,
    input: {} as {
      facility: BffFacilityFieldsFragment | null;
      initialReason?: DigitalVisitReasonEnum;
      stateCode: string;
      restUrl?: string;
      recaptchaSiteKey?: string;
      enableRecaptcha?: boolean;
      disableExternalConfirmation?: boolean;
      preselectedAppointment?: Partial<AppointmentData>;
    },
    emitted: {} as { type: "FACILITY_SWAP_REQUESTED"; code: string },
    meta: {} as StepMeta,
  },
  actions: {
    emitFacilitySwap: emit((_, params: { code: string }) => ({
      type: "FACILITY_SWAP_REQUESTED" as const,
      code: params.code,
    })),
    assignFacilityAndStateCode: assign({
      facility: (_, params: { facility: BffFacilityFieldsFragment | null; stateCode?: string }) =>
        params.facility,
      stateCode: (
        { context },
        params: { facility: BffFacilityFieldsFragment | null; stateCode?: string },
      ) => params.stateCode ?? context.stateCode,
      // Backfill preselected slot metadata the first time its facility resolves.
      appointmentData: (
        { context },
        params: { facility: BffFacilityFieldsFragment | null; stateCode?: string },
      ) => {
        const data = context.appointmentData;
        const facility = params.facility;
        if (!data?.slotId || !facility) {
          return data;
        }

        const location = data.location || facility.name || undefined;
        const facilityCode = data.facilityCode || facility.code || undefined;
        if (location !== data.location || facilityCode !== data.facilityCode) {
          return { ...data, ...(location && { location }), ...(facilityCode && { facilityCode }) };
        }

        return data;
      },
    }),
    assignReason: assign({
      reasonForVisit: (_, params: { reason: DigitalVisitReasonEnum }) => params.reason,
      patientData: ({ context }, params: { reason: DigitalVisitReasonEnum }) =>
        params.reason === DigitalVisitReasonEnum.HealthyMouthMovement
          ? { ...context.patientData, dentalInsurance: "false" }
          : context.patientData,
    }),
    assignPatientData: assign({
      patientData: (_, params: { data: PatientData }) =>
        validatePatientData(params.data).data ?? params.data,
    }),
    assignNeedBasedQuestions: assign({
      needBasedQuestionsAnswers: (_, params: { data: NeedBasedQuestionAnswers }) => params.data,
    }),
    assignNeedBasedQuestionsValid: assign({
      needBasedQuestionsValid: (_, params: { isValid: boolean }) => params.isValid,
    }),
    markPatientSubmitAttempted: assign({
      patientSubmitAttempted: true,
    }),
    assignCombinedPatientContactErrors: assign({
      formErrors: (_, params: { data: PatientData }) => ({
        patientDataErrors: validatePatientData(params.data).errors,
      }),
    }),
    assignGuarantorData: assign({
      guarantorData: (_, params: { data: GuarantorData }) =>
        validateGuarantorData(params.data).data ?? params.data,
    }),
    assignGuarantorErrors: assign({
      formErrors: (_, params: { data: unknown }) => ({
        guarantorDataErrors: validateGuarantorData(params.data).errors,
      }),
    }),
    assignAppointmentData: assign({
      appointmentData: (_, params: { appointmentData: AppointmentData }) => params.appointmentData,
    }),
    assignQuestionsData: assign({
      // For when questions are fetched after reason for visit selection
      questionnaire: (_, params: { questions: QuestionnaireByQuery | null }) => params.questions,
    }),
    assignValidatedPatientResponse: assign({
      validatedPatientResponse: (
        _,
        params: { output: ValidatePatientResponseWithScheduledAppointments | null },
      ) => params.output,
    }),
    markPatientValidationRequested: assign({
      patientValidationRequested: true,
    }),
    resetPatientValidationRequested: assign({
      patientValidationRequested: false,
    }),
    assignScheduleAppointmentResponse: assign({
      scheduleAppointmentResponse: (_, params: { output: any }) => params.output,
    }),
    assignLanguage: assign({
      currentLanguage: (_, params: { language: Language }) => params.language,
    }),
    clearAppointmentData: assign({
      appointmentData: {},
    }),
    clearErrors: assign({ errors: {}, formErrors: {} }),
    clearValidatedPatientResponse: assign({ validatedPatientResponse: null }),
    assignAgeIneligibleError: assign({
      errors: () => ({ errorCode: "ageIneligible" }),
    }),
    clearFieldError: assign({
      formErrors: (
        { context },
        params: {
          key: string;
          field: keyof PatientData | keyof GuarantorData;
        },
      ) => {
        const next = { ...context.formErrors[params.key] };
        delete (next as Record<string, unknown>)[params.field];
        return { ...context.formErrors, [params.key]: next };
      },
    }),
    assignError: assign({
      errors: (_, params: { key: string; message: string; errorCode?: string }) =>
        ({
          [params.key]: params.message,
          ...(params.errorCode && { errorCode: params.errorCode }),
        }) as SchedulerContext["errors"],
    }),
    markStepComplete: assign({
      completedSteps: ({ context }, params: { step: NavigableStep }) => ({
        ...context.completedSteps,
        [params.step]: true as const,
      }),
    }),
    assignSlotsData: assign((_, params: { slots: SlotsSearchResponseV4[] }) => ({
      slots: params.slots,
      slotsError: null,
    })),
    // Spawned rather than invoked so the nearby request starts alongside the primary slots
    // fetch yet outlives the `loadingSlots` -> `idle` transition and settles on its own.
    spawnNearbySlots: enqueueActions(({ enqueue, context }) => {
      enqueue.stopChild("nearbySlots");
      enqueue.spawnChild("fetchNearbySlots", {
        id: "nearbySlots",
        input: buildSlotsSearchInput({ context }),
      });
      enqueue.assign({ nearbySlotsPending: true });
    }),
    stopNearbySlots: enqueueActions(({ enqueue }) => {
      enqueue.stopChild("nearbySlots");
    }),
    assignNearbySlotsData: assign((_, params: { nearbySlots: NearByV4Response[] }) => ({
      nearbySlots: params.nearbySlots,
      nearbySlotsPending: false,
    })),
    clearNearbySlotsData: assign(() => ({
      nearbySlots: [] as NearByV4Response[],
      nearbySlotsPending: false,
    })),
    assignSlotsError: assign((_, params: { error: string }) => ({
      slotsError: params.error,
      slots: [] as SlotsSearchResponseV4[],
      nearbySlots: [] as NearByV4Response[],
      nearbySlotsPending: false,
    })),
    resetSlotsData: assign(() => ({
      slots: [] as SlotsSearchResponseV4[],
      nearbySlots: [] as NearByV4Response[],
      slotsError: null,
      nearbySlotsPending: false,
    })),
    updateSlotsSearchDate: assign({
      slotsSearchDate: (_, params: { startDate: string | undefined }) => params.startDate ?? null,
    }),
    initSlotsSearchDate: assign({
      slotsSearchDate: ({ context }) => context.appointmentData.date ?? null,
    }),
    // Entry action for the confirmation step.
    // - Flag OFF (in-app confirmation): fire-and-forget GTM push.
    // - Flag ON (external navigation): include an `eventCallback` that sends
    //   NAVIGATION_READY back to the machine so `complete.navigating` can advance
    //   to `done` as soon as GTM finishes, without waiting for the 500ms fallback.
    pushConfirmationDataLayer: ({ context, self }) => {
      dataLayerPush(
        {
          event: "appointment_step",
          account_id: context.scheduleAppointmentResponse?.accounts?.[0]?.accountNumber,
          appointment_id: context.scheduleAppointmentResponse?.appointmentNumber,
          ...getAppointmentDataLayerParams(context),
          step_name: DataLayerStepNames.confirmation,
          ...getBaseDataLayerParams(context),
          // Only wire the callback for the external-nav path (flag off/absent). When
          // `disableExternalConfirmation` is on we stay in-app and don't wait on GTM.
          ...(!context.disableExternalConfirmation && {
            eventCallback: () => self.send({ type: "NAVIGATION_READY" }),
          }),
        },
        context.stateCode,
      );
    },
    performNavigation: ({ context }) => {
      const url = buildConfirmationUrl(context.reasonForVisit, context.facility?.address);
      if (url) {
        addBreadcrumb({
          category: "scheduler.confirm_appointment",
          message: "scheduler.confirm_appointment.navigation_started",
          level: "info",
          data: { facility_code: context.facility?.code },
        });
        globalThis.location.assign(url);
      } else {
        captureMessage("scheduler.confirm_appointment.navigation_failed", {
          level: "error",
          tags: { scheduler_flow: getSchedulerFlow(context) },
          extra: {
            facility_code: context.facility?.code,
            reason_for_visit: context.reasonForVisit,
            facility_address: context.facility?.address,
          },
        });
        captureException(
          new Error("buildConfirmationUrl returned null — facility address incomplete"),
          {
            extra: {
              reasonForVisit: context.reasonForVisit,
              facility_code: context.facility?.code,
              facility_address: context.facility?.address,
            },
          },
        );
      }
    },
    writeAppointmentCookie: ({ context }) => {
      writeAppointmentCookieUtil({
        date: context.appointmentData.date,
        time: context.appointmentData.time,
        firstName: context.patientData.firstName ?? "",
      });
    },
    dataLayerPush: ({ context }, params: Record<string, unknown>) => {
      dataLayerPush({ event: "appointment_step", ...params }, context.stateCode);
    },
    pushGbpSchedulingIntent: ({ context }) => {
      const rwgToken = new URLSearchParams(globalThis.location.search).get("rwg_token");
      if (!rwgToken) return;

      dataLayerPush(
        {
          event: "gbp_scheduling_intent",
          brand: getDataLayerBrand(context.reasonForVisit),
          reason_for_visit: context.reasonForVisit,
          reason_for_visit_id:
            context.reasonForVisit && REASON_FOR_VISIT_MAPPING[context.reasonForVisit],
          rwg_token: rwgToken,
          rwg_token_available: true,
          ...(context.facility?.code && {
            website_selected_facility_id: context.facility.code,
          }),
          ...(context.facility?.name && {
            website_selected_facility_name: context.facility.name,
          }),
        },
        context.stateCode,
      );
    },
    captureAppointmentReviewEvent: (
      { context },
      params: { message: string; errorCode?: string },
    ) => {
      captureMessage(params.message, {
        level: params.errorCode ? "warning" : "info",
        tags: {
          scheduler_flow: getSchedulerFlow(context),
          ...(params.errorCode && { error_code: params.errorCode }),
        },
        extra: {
          facility_code: context.facility?.code,
          reason_for_visit: context.reasonForVisit,
          slot_id: context.appointmentData.slotId,
          appointment_date: context.appointmentData.date,
          language: context.currentLanguage,
          is_minor: isPatientUnder18(context),
        },
      });
    },
    capturePatientInformationValidation: (
      { context, event },
      params: { errorType?: string } = {},
    ) => {
      const data = event.type === "SUBMIT_COMBINED_PATIENT_CONTACT" ? event.data : undefined;
      const validationErrors = data ? validatePatientData(data).errors : {};
      const fieldMessages = Object.fromEntries(
        Object.entries(validationErrors).filter(([, message]) => Boolean(message)),
      ) as Record<string, string>;
      const errorType =
        params.errorType ??
        (Object.values(fieldMessages).includes("offensiveInput")
          ? "offensive_input"
          : "field_validation");

      trackFormValidationError({
        formName: "patient_information",
        errorType,
        ...(data && { fields: Object.keys(fieldMessages), fieldMessages }),
        context: {
          scheduler_flow: getSchedulerFlow(context),
          reason_for_visit: context.reasonForVisit ?? undefined,
          facility_code: context.facility?.code,
          language: context.currentLanguage,
        },
      });

      let formErrors: FormValidationErrorItem[] = [];
      if (params.errorType === "nbq_incomplete") {
        formErrors = buildNeedsBasedQuestionValidationErrors(context);
        if (formErrors.length === 0) formErrors = [buildStepValidationError(params.errorType)];
      } else if (params.errorType === "age_ineligible") {
        formErrors = [buildStepValidationError(params.errorType)];
      } else if (data) {
        formErrors = buildFieldValidationErrors(fieldMessages, data);
        if (requiresNeedsBasedQuestions(context) && !context.needBasedQuestionsValid) {
          formErrors = [...formErrors, ...buildNeedsBasedQuestionValidationErrors(context)];
        }
      }
      if (formErrors.length > 0) pushFormValidationError(formErrors, context);
    },
    trackUserEvent: (_, params: { eventType: string; eventData?: Record<string, unknown> }) => {
      void userEvent(params.eventType, params.eventData);
    },
    trackFormEvent: (
      _,
      params: {
        firstName: string;
        lastName: string;
        emailAddress: string;
        dateOfBirth: string;
        reasonForVisit: string;
        phoneNumber: string;
        smsMarketing?: boolean;
        smsTransactional?: boolean;
      },
    ) => {
      void formEvent(params);
    },
  },
  actors: {
    fetchSlots: fetchSlotsActor,

    fetchNearbySlots: fetchNearbySlotsActor,

    fetchQuestions: fetchQuestionsActor,

    scheduleAppointment: scheduleAppointmentActor,

    validatePatient: validatePatientActor,

    transunionFraudCheck: transunionFraudCheckActor,

    verifyRecaptcha: verifyRecaptchaActor,

    getFacilityLocations: getFacilityLocationsActor,
  },
  guards: {
    hasReason: ({ context }) => context.reasonForVisit !== null,
    hasPreselectedAppointment: ({ context }) => !!context.appointmentData?.slotId,
    hasValidGuarantorData: ({ event }) =>
      event.type === "SUBMIT_GUARANTOR_DATA" && validateGuarantorData(event.data).success,
    // The patient schema covers the contact fields too, so this is the whole form.
    isPatientFormValid: ({ event }) =>
      event.type === "SUBMIT_COMBINED_PATIENT_CONTACT" && validatePatientData(event.data).success,
    // Motto and Healthy Mouth Movement don't ask the questions, so they're always complete.
    areQuestionsComplete: ({ context }) =>
      !requiresNeedsBasedQuestions(context) || context.needBasedQuestionsValid,
    hasFacilityCode: ({ context }) => Boolean(context.facility?.code),
    canGoToStep: ({ context, event }, params: { step: NavigableStep }) =>
      event.type === "GOTO_STEP" &&
      event.step === params.step &&
      context.completedSteps[params.step] === true &&
      // Once the appointment is booked, back-nav is locked. `markStepComplete` for
      // appointmentReview fires on `submitting.onDone` (right before entering `confirmation`),
      // so this flag is a reliable "user has booked" signal without needing snapshot access.
      context.completedSteps.appointmentReview !== true,
    isPatientUnder18: ({ context }) => isPatientUnder18(context),
    isAgeIneligibleForReason: ({ context, event }) => {
      const dob =
        event.type === "SUBMIT_COMBINED_PATIENT_CONTACT"
          ? event.data.dateOfBirth
          : context.patientData?.dateOfBirth;
      return isAgeIneligibleForReason(dob, context.reasonForVisit);
    },
    isSlotFirstFlow: ({ context }) => isSlotFirstFlow(context),
    requiresNeedsBasedQuestions: ({ context }) => requiresNeedsBasedQuestions(context),
    hasNotRequestedPatientValidation: ({ context }) => !context.patientValidationRequested,
    isRecaptchaEnabled: ({ context }) => context.enableRecaptcha,
    // External navigation is the default; the `disableExternalConfirmation` flag opts INTO
    // the in-app confirmation state instead. Guard returns true when we should perform
    // the external nav path (GTM eventCallback race → performNavigation).
    isExternalConfirmationEnabled: ({ context }) => !context.disableExternalConfirmation,
  },
});
